import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { StreamMode } from '@br/protocol';
import { FrameStore } from './frames';
import { ViewerSocket, type ViewerEvent, type ViewerState } from './socket';

export interface UseViewerOptions {
  tabId?: string;
  supportSessionId?: string;
  mode?: StreamMode;
  onEvent?: (e: ViewerEvent) => void;
}

export interface ViewerHandle {
  socket: ViewerSocket;
  frames: FrameStore;
  state: ViewerState;
}

/** Owns one viewer socket + frame store for the lifetime of the component (per profile/session). */
export function useViewer(profileId: string, opts: UseViewerOptions = {}): ViewerHandle {
  const onEventRef = useRef(opts.onEvent);
  onEventRef.current = opts.onEvent;
  const initialTab = useRef(opts.tabId);
  const initialMode = useRef(opts.mode);

  const { socket, frames } = useMemo(() => {
    const frames = new FrameStore();
    const socket = new ViewerSocket({
      profileId,
      supportSessionId: opts.supportSessionId,
      tabId: initialTab.current,
      mode: initialMode.current,
      frames,
      onEvent: (e) => onEventRef.current?.(e),
    });
    return { socket, frames };
  }, [profileId, opts.supportSessionId]);

  useEffect(() => {
    socket.connect();
    return () => {
      socket.close();
    };
  }, [socket]);

  useEffect(() => () => frames.dispose(), [frames]);

  const state = useSyncExternalStore(socket.subscribe, socket.getState, socket.getState);
  return { socket, frames, state };
}
